import { useEffect, useMemo, useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import type { Row } from '@tanstack/react-table';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import { evalFetchLoadableAtom, lastEvalParamsAtom } from './atoms';
import { useEvalSelection } from './hooks';
import { formatSource } from './utils';
import { LinkOrText } from '../link-or-text';
import { EvaluationRowDetail } from './evaluation-row-detail';
import type { EvalUnifiedResult } from './types';
import style from './wizard.module.scss';

const INITIAL_PAGE_SIZE = DEFAULT_PAGE_SIZE;

/**
 * Left-column selection table for the Compare Evaluations page — an
 * instruction block plus a paginated evaluation DataTable with checkbox
 * selection (max 2). Writes the picked pair to selectedEvalsAtom, which the
 * Current Selections sidebar and the Compare button read. Uses
 * mutationAtomWithLoadable for server-side data fetching.
 * @return {JSX.Element} The evaluation selection table
 */
export const SelectEvaluationsStep = () => {
  const [loadableState, fetchEvals] = useAtom(evalFetchLoadableAtom);
  const lastParams = useAtomValue(lastEvalParamsAtom);
  const setLastParams = useSetAtom(lastEvalParamsAtom);

  // Derive data early so useEvalSelection can resolve IDs to objects
  const isLoading = loadableState.state === 'loading';
  const data =
    loadableState.state === 'hasData' && loadableState.data
      ? loadableState.data.results
      : [];
  const totalCount =
    loadableState.state === 'hasData' && loadableState.data
      ? loadableState.data.totalCount
      : 0;
  const error =
    loadableState.state === 'hasError' ? (loadableState.error as Error) : null;

  const { selectedEvals, handleRowSelect } = useEvalSelection(data);

  const columns: DataTableColumnDef<EvalUnifiedResult>[] = useMemo(
    () => [
      {
        id: 'evalId',
        accessorKey: 'evalId',
        header: 'Eval ID',
        cell: (info) => {
          const row = info.row.original;
          const val = info.getValue() as number;
          return (
            <LinkOrText href={row.href} className={style.link}>
              {val}
            </LinkOrText>
          );
        },
      },
      {
        id: 'scenario_name',
        accessorFn: (row: EvalUnifiedResult) => row.scenario?.name,
        header: 'Scenario',
        cell: (info) => {
          const row = info.row.original;
          const val = info.getValue() as string | undefined;
          return (
            <LinkOrText href={row.scenario?.href} className={style.link}>
              {val ?? '--'}
            </LinkOrText>
          );
        },
      },
      {
        id: 'interestType',
        accessorKey: 'interestType',
        header: 'Interest Type',
      },
      {
        id: 'package_name',
        accessorFn: (row: EvalUnifiedResult) => row.package?.name,
        header: 'Package',
        cell: (info) => {
          const row = info.row.original;
          const val = info.getValue() as string | undefined;
          return (
            <LinkOrText href={row.package?.href} className={style.link}>
              {val ?? '--'}
            </LinkOrText>
          );
        },
      },
      {
        id: 'sellerName',
        accessorKey: 'sellerName',
        header: 'Seller',
      },
      {
        id: 'acreage',
        accessorKey: 'acreage',
        header: 'Acreage',
        meta: { type: 'decimal' as const },
      },
      {
        id: 'source',
        accessorKey: 'source',
        header: 'Source',
        cell: (info) => <span>{formatSource(info.getValue() as string)}</span>,
      },
    ],
    []
  );

  /**
   * Server-driven data loading callback passed to DataTable.
   * Persists params so DataTable state can be restored on remount.
   * @param params - Current pagination, sorting, filter, and search state
   */
  const loadData = useCallback(
    (params: TableState) => {
      setLastParams(params);
      fetchEvals(params);
    },
    [fetchEvals, setLastParams]
  );

  // Initial fetch on mount — skip if atom already has data (e.g., remounts)
  useEffect(() => {
    if (loadableState.state === 'hasData' && loadableState.data) return;
    const params: TableState = lastParams ?? {
      page: 1,
      pageSize: INITIAL_PAGE_SIZE,
      // Default to Source descending so 3.0 evaluations surface first (2.0 is
      // being retired); the backend appends eval_id desc as the tiebreaker, so
      // within each source the newest evaluations still come first.
      sorting: [{ id: 'source', desc: true }],
      globalFilter: '',
      columnFilters: [],
      selection: { selectAll: false, toggledNodes: [] },
      expanded: {},
    };
    setLastParams(params);
    fetchEvals(params);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderRowSubComponent = useCallback(
    (row: Row<EvalUnifiedResult>) => <EvaluationRowDetail row={row.original} />,
    []
  );

  return (
    <>
      <p className={style.instructionText}>
        <strong>Select two evaluations to compare.</strong> The first evaluation
        you select will serve as the baseline. The second will be compared
        against it. The differences shown will reflect how the first selected
        evaluation differs from the second selected evaluation.{' '}
        <em>
          Order matters. Switching the order changes the comparison results.
        </em>
      </p>
      <div className={style.tableWrapper}>
        <DataTable<EvalUnifiedResult>
          data={data}
          columns={columns}
          getRowId={(row) => `${row.evalId}_${row.source}`}
          isLoading={isLoading}
          error={error}
          scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
          onPaginationChange={loadData}
          onSortChange={loadData}
          onFilterChange={loadData}
          onGlobalSearchChange={loadData}
          initialTableState={{
            page: lastParams?.page ?? 1,
            pageSize: lastParams?.pageSize ?? INITIAL_PAGE_SIZE,
            sortColumn: lastParams?.sorting[0]?.id ?? 'source',
            sortDirection:
              lastParams?.sorting[0]?.desc === false ? 'asc' : 'desc',
            globalFilter: lastParams?.globalFilter ?? '',
            columnFilters: lastParams?.columnFilters ?? [],
          }}
          // Controlled selection: `selectedEvals` (the picked pair) is the source
          // of truth; the table reflects it and reports clicks via `onSelectionChange`.
          controlledState={{
            selection: {
              selectAll: false,
              toggledNodes: selectedEvals.map((e) => `${e.evalId}_${e.source}`),
            },
          }}
          onSelectionChange={(s) => handleRowSelect(s.selection)}
          renderRowSubComponent={renderRowSubComponent}
          searchPlaceholder="Search by Package Name, Evaluation ID"
          config={{
            selectionType: 'multi',
            maxSelections: 2,
            globalSearch: true,
            sorting: true,
            columnFilters: true,
            columnResizing: true,
            columnVisibility: true,
            columnReordering: true,
            columnPinning: true,
            export: false,
            hideFooter: false,
            expandAllToggle: true,
          }}
        />
      </div>
    </>
  );
};
